import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { AppColorKeys } from '@fc-core/constants/colors';
import { AppIconsType } from '@fc-shared/ui/icon/icons';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { ButtonComponent } from '@fc-shared/ui/buttons/button.component';
import { isMobile } from '@fc-shared/utils/is-mobile';

@Component({
  selector: 'fc-empty-state',
  standalone: true,
  imports: [IconComponent, ButtonComponent],
  host: { '[attr.title]': 'null' },
  template: `
    <div
      class="flex flex-col items-center justify-center gap-12 text-center p-12"
    >
      <div
        class="flex flex-col items-center"
        [class]="sizeLarge() ? 'gap-10' : 'gap-8'"
      >
        @if (icon()) {
          <div
            class="flex items-center justify-center rounded-full {{
              sizeLarge() ? 'size-56' : 'size-40'
            }} {{ palette().iconBackground }}"
          >
            <fc-icon
              [icon]="icon()"
              [color]="palette().icon"
              [size]="sizeLarge() ? 32 : 20"
            />
          </div>
        }
        <div class="flex flex-col gap-4 items-center">
          @if (title()) {
            <div
              class="font-medium capitalize {{
                sizeLarge() ? 'text-base' : 'text-sm'
              }} {{ palette().title }}"
            >
              {{ title() }}
            </div>
          }
          @if (subtitle()) {
            <div class="text-sm {{ palette().subtitle }}">
              {{ subtitle() }}
            </div>
          }
        </div>
      </div>
      <div class="flex flex-row gap-12">
        <ng-content></ng-content>
        @if (buttonText()) {
          <fc-button
            (click)="buttonClicked.emit()"
            [icon]="buttonIcon()"
            [size]="'md'"
          >
            {{ buttonText() }}
          </fc-button>
        }
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmptyStateComponent {
  icon = input<AppIconsType>('icon-empty_state_history');
  title = input<string>();
  size = input<'sm' | 'lg'>('lg');
  subtitle = input<string>();
  buttonText = input<string>();
  buttonIcon = input<AppIconsType>('icon-add');
  buttonClicked = output<void>();
  isMobile = isMobile;
  sizeLarge = computed(() => this.size() === 'lg' && !this.isMobile());
  appearance = input<'default' | 'on-media' | 'success'>('default');

  protected palette = computed(() => {
    switch (this.appearance()) {
      case 'on-media':
        return {
          icon: 'white' as AppColorKeys,
          iconBackground: 'bg-secondary-600',
          title: 'text-grey-50',
          subtitle: 'text-grey-50 max-w-420',
        };
      case 'success':
        return {
          icon: 'success600' as AppColorKeys,
          iconBackground: 'bg-success-100',
          title: 'text-grey-900',
          subtitle: 'text-grey-600',
        };
      default:
        return {
          icon: 'secondary500' as AppColorKeys,
          iconBackground: 'bg-secondary-100',
          title: 'text-grey-900',
          subtitle: 'text-grey-600 max-w-270',
        };
    }
  });
}
